// The paragraphs a customer signs (BL-052): the authorisation, signed to approve the work on an
// estimate, and the acknowledgement, signed to accept the finished work on an invoice. Each is the
// company's or a department's text with merge fields in braces, as ServiceTitan's are ({Total},
// {Terms}). Filled once, at signing, and kept as signed: the office, the portal and the iPad fill
// them the same way, and line breaks are the text's own (it's the legal wording, never reflowed).

export type ParagraphKind = 'authorisation' | 'acknowledgement';

/** The fields each paragraph may use, with what each is, for the settings screen. */
export const PARAGRAPH_FIELDS: Record<ParagraphKind, readonly (readonly [string, string])[]> = {
  authorisation: [
    ['Company', 'the department’s official name, else the company’s'],
    ['Customer', 'the customer’s name'],
    ['Total', 'the chosen option’s total'],
    ['AuthorizedAmount', 'the amount the customer approves (the chosen option’s total)'],
    ['Terms', 'their payment terms, like “Net 30” or “Due on receipt”'],
    ['EstimateNumber', 'the estimate’s number'],
  ],
  acknowledgement: [
    ['Company', 'the department’s official name, else the company’s'],
    ['Customer', 'the customer’s name'],
    ['Total', 'the invoice’s total'],
    ['Terms', 'their payment terms, like “Net 30” or “Due on receipt”'],
    ['InvoiceNumber', 'the invoice’s number'],
  ],
};

/**
 * Each paragraph's text when neither the company nor the department has set one: the settings'
 * defaults, here so the iPad fills the same words offline.
 */
export const DEFAULT_PARAGRAPHS: Record<ParagraphKind, string> = {
  authorisation:
    'Prices are fixed for 30 days. Work is scheduled with you by phone. Warranty as listed for the option chosen. ' +
    'Payment is due when the work is complete. You can cancel before the work is booked at no charge.',
  acknowledgement:
    'I confirm the work on invoice {InvoiceNumber} was completed to my satisfaction, and I agree to pay {Total} ({Terms}).',
};

/** {Terms} in words: "Net 30", or "Due on receipt" with none. */
export const termsWords = (days: number | null): string => (days === null ? 'Due on receipt' : `Net ${days}`);

const FIELD = /\{([A-Za-z][A-Za-z0-9]*)\}/g;

/** The fields a text uses that its paragraph doesn't have, each once, in order. */
export function unknownParagraphFields(text: string, kind: ParagraphKind): string[] {
  const known = new Set(PARAGRAPH_FIELDS[kind].map(([k]) => k));
  return [...new Set([...text.matchAll(FIELD)].map((m) => m[1]!))].filter((k) => !known.has(k));
}

/**
 * The paragraph with its fields filled; a field with no value reads as nothing, or, for a preview
 * (`keep`), stays in its braces so a mistyped one shows where it is.
 */
export function fillParagraph(text: string, values: Readonly<Record<string, string>>, keep = false): string {
  return text.replace(FIELD, (whole, k: string) => values[k] ?? (keep ? whole : ''));
}

/**
 * {Total} as the iPad fills it when the invoice has changes waiting to sync and no signal: the
 * amount isn't known for sure, so the paragraph says so in these words (the lead's ruling, BL-052).
 */
export const OFFLINE_TOTAL_WORDS = 'the total on the invoice we send you';
